import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { useOrganizationRoleDistribution } from "@/hooks/peo-organizations/useOrganizationRoleDistribution";
import { Users } from "lucide-react";

interface RoleDistributionCardProps {
  providerId: string;
}

const getRoleColor = (index: number) => {
  const colors = [
    'bg-blue-500',
    'bg-green-500', 
    'bg-purple-500',
    'bg-yellow-500',
    'bg-red-500',
    'bg-indigo-500',
    'bg-pink-500',
    'bg-orange-500'
  ];
  return colors[index % colors.length];
};

export const RoleDistributionCard = ({ providerId }: RoleDistributionCardProps) => {
  const { roleDistribution, totalMembers, isLoading, error } = useOrganizationRoleDistribution(providerId);

  if (error) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Role Distribution</CardTitle>
          <CardDescription>Current role assignments</CardDescription>
        </CardHeader>
        <CardContent>
          <p className="text-center text-muted-foreground text-sm">
            Failed to load role distribution
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Role Distribution</CardTitle>
        <CardDescription>Current role assignments</CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        {isLoading ? (
          <div className="space-y-3">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="flex items-center justify-between animate-pulse">
                <div className="h-4 bg-muted rounded w-1/3"></div>
                <div className="flex items-center gap-2">
                  <div className="h-6 bg-muted rounded w-8"></div>
                  <div className="w-20 h-2 bg-muted rounded-full"></div>
                </div>
              </div>
            ))}
          </div>
        ) : roleDistribution.length === 0 ? (
          <div className="flex items-center justify-center py-8 text-muted-foreground">
            <div className="text-center">
              <Users className="h-8 w-8 mx-auto mb-2 opacity-50" />
              <p className="text-sm">No roles assigned yet</p>
            </div>
          </div>
        ) : (
          roleDistribution.map((role, index) => (
            <div key={role.role_name} className="flex items-center justify-between">
              <span className="text-sm font-medium truncate flex-1 mr-2">{role.role_name}</span>
              <div className="flex items-center gap-2">
                <Badge variant="secondary" className="text-xs">
                  {role.member_count}
                </Badge>
                <div className="w-20 h-2 bg-muted rounded-full">
                  <div 
                    className={`h-2 rounded-full ${getRoleColor(index)}`}
                    style={{ width: `${Math.max(role.percentage, 5)}%` }}
                  ></div>
                </div>
                <span className="text-xs text-muted-foreground w-10 text-right">
                  {role.percentage.toFixed(0)}%
                </span>
              </div>
            </div>
          ))
        )}
        {!isLoading && roleDistribution.length > 0 && (
          <div className="pt-2 border-t">
            <div className="flex items-center justify-between text-xs text-muted-foreground">
              <span>Total Members</span>
              <span className="font-medium">{totalMembers}</span>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};